import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { StaffAllocationGrid, GridType, GridTemplate, MultiGridView, GridStats, CrossGridConflict, LineDefinition } from '../types/grid';
import { Department } from '../types/data';
import { getDefaultGridTypes, calculateTeacherTotalMinutes, checkCrossGridConflicts } from '../utils/gridTypeUtils';
import { useAcademicYear } from './AcademicYearContext';
// TODO: MIGRATION NEEDED - Replace Firebase with Supabase
// import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, onSnapshot, query, where, setDoc } from 'firebase/firestore';
// import { db } from '../firebase/config';
import { supabase } from '../supabase/config';
import { setLineAssignment, setLineAssignmentsBulk } from '../services/lineAssignmentService';
import { assignmentEvents } from '../utils/assignmentEvents';
import { transformGridRow } from '../utils/transformGrid';
import { useGridsData } from './GridsDataContext';

export interface MultiGridContextType {
  // Grid Management
  allGrids: StaffAllocationGrid[];
  gridsForYear: StaffAllocationGrid[]; // Grids filtered by current working year
  activeGrids: StaffAllocationGrid[];
  currentGrid: StaffAllocationGrid | null;
  gridTypes: GridType[];
  gridTemplates: GridTemplate[];

  // View State
  multiGridView: MultiGridView;
  setMultiGridView: (view: MultiGridView) => void;
  currentGridId: string | null;
  setCurrentGridId: (gridId: string | null) => void;

  // Grid Operations
  createGrid: (name: string, defaultClassMinutes: number, lines: string[], studyPeriods: { id: string; name: string; lines: string[] }[], templateId?: string) => Promise<string>;
  updateGrid: (gridId: string, updates: Partial<StaffAllocationGrid>) => Promise<void>;
  deleteGrid: (gridId: string) => Promise<void>;
  cloneGrid: (gridId: string, newName: string) => Promise<string>;

  // Grid Type Management
  createGridType: (gridType: Omit<GridType, 'id'>) => Promise<string>;
  updateGridType: (gridTypeId: string, updates: Partial<GridType>) => Promise<void>;
  deleteGridType: (gridTypeId: string) => Promise<void>;
  canDeleteGridType: (gridTypeId: string) => boolean;

  // Teacher Assignment Operations
  assignTeacher: (gridId: string, classId: string, lineId: string, teacherCode: string | null) => Promise<void>;
  batchAssignTeachers: (gridId: string, assignments: Array<{ classId: string; lineId: string; teacherCode: string | null }>) => Promise<void>;
  validateCrossGridAssignment: (gridId: string, classId: string, lineId: string, teacherCode: string) => Promise<{ valid: boolean; conflicts: string[] }>;

  // Analytics
  getGridStats: (gridId: string) => GridStats | null;
  getAllGridsStats: () => GridStats;
  getCrossGridConflicts: (teacherCode?: string) => CrossGridConflict[];

  // Loading States
  isLoading: boolean;
  error: string | null;
}

export const MultiGridContext = createContext<MultiGridContextType | undefined>(undefined);

// Helper function to determine study period based on line ID
function determineStudyPeriod(lineId: string): string {
  if (!lineId) return 'semester1'; // Default for undefined/null
  const id = (lineId || '').toLowerCase();

  // Check for explicit semester markers
  if (id.includes('s1') || id.includes('sem1') || id.includes('semester1')) {
    return 'semester1';
  }
  if (id.includes('s2') || id.includes('sem2') || id.includes('semester2')) {
    return 'semester2';
  }

  // Traditional line mappings
  if (['a', 'b', 'c', 'd', 'e', 'f'].includes(id)) {
    return 'semester1';
  }
  if (['g', 'h', 'i', 'j', 'k', 'l'].includes(id)) {
    return 'semester2';
  }

  // For custom lines like O1, O2, determine by position or naming
  if (id.endsWith('1') && !id.includes('s2')) {
    return 'semester1';
  }
  if (id.endsWith('2') && !id.includes('s1')) {
    return 'semester2';
  }

  // Default fallback - assume semester1 for unknown patterns
  return 'semester1';
}

interface MultiGridProviderProps {
  children: ReactNode;
  departments?: Department[];
}

export function MultiGridProvider({ children, departments = [] }: MultiGridProviderProps) {
  // Academic year context for year filtering
  let workingYear: number;
  try {
    const academicYearContext = useAcademicYear();
    workingYear = academicYearContext.workingYear;
  } catch (e) {
    // Fallback if AcademicYearContext is not available
    workingYear = new Date().getFullYear();
  }

  const [allGrids, setAllGrids] = useState<StaffAllocationGrid[]>([]);
  const [currentGridId, setCurrentGridId] = useState<string | null>(null);
  const [gridTypes, setGridTypes] = useState<GridType[]>([]);
  const [gridTemplates, setGridTemplates] = useState<GridTemplate[]>([]);
  // Initialize with guaranteed safe default state
  const [multiGridView, setMultiGridViewState] = useState<MultiGridView>(() => ({
    activeGridIds: [],
    viewMode: 'tabs' as const
  }));
  
  // Enhanced setMultiGridView with validation
  const setMultiGridView = (view: MultiGridView) => {
    try {
      // Ensure activeGridIds is always a valid array with proper methods
      let safeActiveGridIds: string[] = [];
      if (Array.isArray(view?.activeGridIds)) {
        try {
          // Test if the array has the includes method and it works
          safeActiveGridIds = view.activeGridIds.slice(); // Create a new array
          if (typeof safeActiveGridIds.includes !== 'function') {
            safeActiveGridIds = [...view.activeGridIds];
          }
        } catch (e) {
          safeActiveGridIds = [];
        }
      }

      const safeView: MultiGridView = {
        activeGridIds: safeActiveGridIds,
        viewMode: view?.viewMode || 'tabs'
      };

      setMultiGridViewState(safeView);
    } catch (error) {
      // Fallback to safe state
      setMultiGridViewState({
        activeGridIds: [],
        viewMode: 'tabs'
      });
    }
  };
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadGridTypes = async () => {
      try {
        const { data, error } = await supabase
          .from('grid_types')
          .select('*')
          .order('name');
        
        if (error) {
          setGridTypes(getDefaultGridTypes());
          return;
        }
        
        // Map database grid types to expected format
        const mappedGridTypes = (data || []).map(dbGridType => {
          // Extract from structure field where we stored the TypeScript-compatible data
          const structure = dbGridType.structure || {};
          
          return {
            id: dbGridType.id,
            name: dbGridType.name,
            description: dbGridType.description || '',
            // Map from structure field to match TypeScript GridType interface
            defaultClassMinutes: structure.default_class_minutes || 70,
            linePrefix: structure.line_prefix || 'A-F,G-L',
            availableLines: structure.available_lines || ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'],
            departmentOverrides: structure.department_overrides || [],
            isSystemDefault: structure.is_system_default || false,
            createdAt: dbGridType.created_at ? new Date(dbGridType.created_at) : undefined,
            updatedAt: dbGridType.updated_at ? new Date(dbGridType.updated_at) : undefined
          };
        });
        
        setGridTypes(mappedGridTypes.length > 0 ? mappedGridTypes : getDefaultGridTypes());
      } catch (error) {
        setGridTypes(getDefaultGridTypes());
      }
    };
    
    loadGridTypes();
  }, []);

  // Race condition protection
  const [isLoadingGrids, setIsLoadingGrids] = useState(false);

  // Consume the shared grids data source (one fetch + one subscription, owned by
  // GridsDataProvider) instead of running our own. Derive allGrids from the raw
  // rows with the canonical transform + this context's grid_types augmentation,
  // ordered by created_at desc (preserving the previous `.order('created_at')`).
  // Mutations below are unchanged.
  const { rawGrids, error: gridsDataError, refetch: refetchGridsData } = useGridsData();
  useEffect(() => {
    try {
      const grids = (rawGrids || [])
        .slice()
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
        .map(grid => {
          const base = transformGridRow(grid);
          return {
            ...base,
            gridType: grid.grid_types || (gridTypes.length > 0 ? gridTypes[0] : getDefaultGridTypes()[0]),
          };
        });

      setAllGrids(grids);

      // Auto-select first grid if no current grid is selected
      if (!currentGridId && grids.length > 0) {
        setCurrentGridId(grids[0].id);
      }

      setError(gridsDataError);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error');
      setAllGrids([]);
    } finally {
      setIsLoading(false);
    }
    // currentGridId omitted: auto-select should run only while none is set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawGrids, gridsDataError, gridTypes]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const { data, error } = await supabase
          .from('grid_templates')
          .select('*')
          .order('name');
        
        if (error) {
          return;
        }
        
        const templates = data?.map(template => ({
          id: template.id,
          name: template.name,
          description: template.description || '',
          defaultClassMinutes: template.default_class_minutes || 70,
          lines: template.lines || ['A', 'B', 'C', 'D', 'E', 'F'],
          studyPeriods: template.study_periods || [
            { id: 'semester1', name: 'Semester 1', lines: ['A', 'B', 'C'] },
            { id: 'semester2', name: 'Semester 2', lines: ['D', 'E', 'F'] }
          ],
          curriculumAreas: template.curriculum_areas || ['ENGLISH', 'MATHEMATICS', 'SCIENCE', 'HUMANITIES', 'HPE', 'STEAM', 'ARTS'],
          createdAt: new Date(template.created_at),
          updatedAt: template.updated_at ? new Date(template.updated_at) : new Date(template.created_at)
        })) || [];
        
        // If no templates exist, use defaults
        if (templates.length === 0) {
          // Templates will be empty array
        }
        
        setGridTemplates(templates);
      } catch (error) {
        setGridTemplates([]);
      }
    };
    
    loadTemplates();
  }, []);

  const currentGrid = allGrids.find(grid => grid.id === currentGridId) || null;
  const activeGrids = allGrids.filter(grid => grid.isActive);

  // Filter grids by current working year
  const gridsForYear = useMemo(() => {
    return allGrids.filter(grid => grid.year === workingYear);
  }, [allGrids, workingYear]);

  // Defensive wrapper for setMultiGridView
  const safeSetMultiGridView = (view: MultiGridView) => {
    try {
      if (!view) {
        setMultiGridView({
          activeGridIds: [],
          viewMode: 'tabs'
        });
        return;
      }

      // Ensure we have a valid view object
      const safeView = {
        activeGridIds: Array.isArray(view.activeGridIds) ? view.activeGridIds : [],
        viewMode: view.viewMode || 'tabs'
      };

      // Test the activeGridIds array for proper methods
      if (Array.isArray(safeView.activeGridIds) && typeof safeView.activeGridIds.includes === 'function') {
        setMultiGridView(safeView);
      } else {
        setMultiGridView({
          activeGridIds: Array.isArray(view.activeGridIds) ? [...view.activeGridIds] : [],
          viewMode: view.viewMode || 'tabs'
        });
      }
    } catch (error) {
      setMultiGridView({
        activeGridIds: [],
        viewMode: 'tabs'
      });
    }
  };

  const createGrid = async (name: string, defaultClassMinutes: number, lines: string[], studyPeriods: { id: string; name: string; lines: string[] }[], templateId?: string): Promise<string> => {
    try {
      console.log('📝 MultiGridContext: Starting grid creation');
      console.log('   - Name:', name);
      console.log('   - Minutes per class:', defaultClassMinutes);
      console.log('   - Lines:', lines);
      console.log('   - Study periods:', studyPeriods.length);

      // Validation: Check required parameters
      if (!name || name.trim().length === 0) {
        throw new Error('Grid name is required');
      }

      if (!defaultClassMinutes || defaultClassMinutes <= 0) {
        throw new Error('Valid class duration is required');
      }

      if (!lines || lines.length === 0) {
        throw new Error('At least one line is required');
      }

      if (!studyPeriods || studyPeriods.length === 0) {
        throw new Error('At least one study period is required');
      }

      // Get template data if templateId is provided
      let templateData = null;
      if (templateId) {
        console.log('   - Using template:', templateId);
        const template = gridTemplates.find(t => t.id === templateId);
        if (template) {
          templateData = {
            lines: template.lines,
            study_periods: template.studyPeriods,
            curriculum_areas: template.curriculumAreas,
            default_class_minutes: template.defaultClassMinutes
          };
          console.log('   - Template found and loaded');
        } else {
          console.warn('   - Template not found, using provided parameters');
        }
      }

      // Get curriculum areas: from template, or Main Grid, or default list
      let curriculumAreas = templateData?.curriculum_areas;
      if (!curriculumAreas || curriculumAreas.length === 0) {
        // Try to get curriculum areas from Main Grid
        const mainGrid = grids.find(g => g.name === 'Main Grid');
        if (mainGrid && mainGrid.curriculumAreas && mainGrid.curriculumAreas.length > 0) {
          curriculumAreas = mainGrid.curriculumAreas;
          console.log('   - Using curriculum areas from Main Grid');
        } else {
          // Fall back to default curriculum areas
          const defaultCurriculumAreas = [
            { id: 'agriculture', name: 'AGRICULTURE' },
            { id: 'arts', name: 'ARTS' },
            { id: 'english', name: 'ENGLISH' },
            { id: 'hpe', name: 'HPE' },
            { id: 'humanities', name: 'HUMANITIES' },
            { id: 'languages', name: 'LANGUAGES' },
            { id: 'learning_support', name: 'LEARNING SUPPORT' },
            { id: 'mathematics', name: 'MATHEMATICS' },
            { id: 're', name: 'RE' },
            { id: 'science', name: 'SCIENCE' },
            { id: 'steam', name: 'STEAM' },
            { id: 'technology', name: 'TECHNOLOGY' },
            { id: 'vm_vet', name: 'VM/VET' }
          ];
          curriculumAreas = defaultCurriculumAreas;
          console.log('   - Using default curriculum areas');
        }
      }

      const gridData = {
        name: name.trim(),
        description: `Grid created from ${templateId ? 'template' : 'scratch'}`,
        // Store grid-specific data in grid_data field to match current schema
        grid_data: {
          default_class_minutes: templateData?.default_class_minutes || defaultClassMinutes,
          lines: templateData?.lines || lines,
          study_periods: templateData?.study_periods || studyPeriods
        },
        curriculum_areas: curriculumAreas,
        classes: [],
        grid_type_id: gridTypes[0]?.id || null,
        is_active: true,
        academic_year: workingYear, // Use current working year for new grids
        status: 'draft',
        settings: {
          default_class_minutes: templateData?.default_class_minutes || defaultClassMinutes,
          lines: templateData?.lines || lines,
          study_periods: templateData?.study_periods || studyPeriods
        }
      };

      console.log('💾 MultiGridContext: Inserting grid into database...');

      const { data, error } = await supabase
        .from('grids')
        .insert([gridData])
        .select()
        .single();

      if (error) {
        console.error('❌ Database error:', error);
        // Provide more specific error messages based on error type
        if (error.code === '23505') {
          throw new Error('A grid with this name already exists. Please choose a different name.');
        } else if (error.code === '23503') {
          throw new Error('Invalid grid type reference. Please try again.');
        } else {
          throw new Error(`Database error: ${error.message || 'Unknown error occurred'}`);
        }
      }

      if (!data || !data.id) {
        throw new Error('Grid was created but no ID was returned. Please refresh the page.');
      }

      console.log('✅ MultiGridContext: Grid created successfully with ID:', data.id);
      // Refresh the shared grids source so the new grid appears immediately
      // (realtime isn't relied upon — pit tables aren't in the publication).
      await refetchGridsData();
      return data.id;
    } catch (error) {
      console.error('❌ MultiGridContext: Failed to create grid:', error);

      // Re-throw with more context if it's a generic error
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('An unexpected error occurred while creating the grid');
      }
    }
  };

  const updateGrid = async (gridId: string, updates: Partial<StaffAllocationGrid>): Promise<void> => {
    try {
      
      // Get current grid data to preserve structure
      const { data: currentGrid } = await supabase
        .from('grids')
        .select('grid_data, settings, version')
        .eq('id', gridId)
        .single();

      const currentGridData = currentGrid?.grid_data || {};
      const currentSettings = currentGrid?.settings || {};
      const baseVersion = (currentGrid as any)?.version as number | null | undefined;
      
      const updateData: any = {};
      
      // Map StaffAllocationGrid properties to database columns
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.curriculumAreas !== undefined) updateData.curriculum_areas = updates.curriculumAreas;
      if (updates.classes !== undefined) updateData.classes = updates.classes;
      if (updates.isActive !== undefined) updateData.is_active = updates.isActive;
      if (updates.gridType !== undefined) updateData.grid_type_id = updates.gridType.id;

      // Study periods are `semesters` on StaffAllocationGrid. `studyPeriods` was
      // read here historically and matches no field on the type, so every
      // study-period edit made through updateGrid was silently dropped.
      const studyPeriods = updates.semesters;

      // Handle grid_data field updates.
      //
      // Classes MUST be written to grid_data.classes as well as the `classes`
      // column: transformGridRow prefers grid_data.classes when it is non-empty,
      // so writing only the column leaves grids created by CloneGridsToYear
      // (which seeds grid_data.classes) showing stale or zero classes. Keeping
      // both in step here is what stops the two from diverging.
      if (
        updates.classes !== undefined ||
        updates.defaultClassMinutes !== undefined ||
        updates.lines !== undefined ||
        studyPeriods !== undefined
      ) {
        updateData.grid_data = {
          ...currentGridData,
          ...(updates.classes !== undefined && { classes: updates.classes }),
          ...(updates.defaultClassMinutes !== undefined && { default_class_minutes: updates.defaultClassMinutes }),
          ...(updates.lines !== undefined && { lines: updates.lines }),
          ...(studyPeriods !== undefined && { study_periods: studyPeriods })
        };
      }

      // `settings` mirrors the grid's structure only — it never carries classes.
      if (
        updates.defaultClassMinutes !== undefined ||
        updates.lines !== undefined ||
        studyPeriods !== undefined
      ) {
        updateData.settings = {
          ...currentSettings,
          ...(updates.defaultClassMinutes !== undefined && { default_class_minutes: updates.defaultClassMinutes }),
          ...(updates.lines !== undefined && { lines: updates.lines }),
          ...(studyPeriods !== undefined && { study_periods: studyPeriods })
        };
      }
      
      
      // Scoped to the version read above: a whole-array write that would
      // overwrite someone else's change fails instead of silently winning
      // (issue #218). Cell assignments no longer come through this path.
      let query = supabase.from('grids').update(updateData).eq('id', gridId);
      if (typeof baseVersion === 'number') {
        query = query.eq('version', baseVersion);
      }
      const { data: updatedRows, error } = await query.select('id');

      if (error) throw error;

      if (typeof baseVersion === 'number' && (updatedRows?.length ?? 0) === 0) {
        await refetchGridsData();
        throw new Error(
          'This grid was changed by someone else while you were editing. Your change was not saved — the latest version has been reloaded, please try again.'
        );
      }

      // Refresh the shared grids source so the change is reflected immediately
      // (previously relied on realtime, which is dead for pit tables post-repoint).
      await refetchGridsData();

    } catch (error) {
      throw error;
    }
  };

  const deleteGrid = async (gridId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('grids')
        .delete()
        .eq('id', gridId);
      
      if (error) throw error;
      
      // Update local state
      if (currentGridId === gridId) {
        const remainingGrids = allGrids.filter(g => g.id !== gridId);
        setCurrentGridId(remainingGrids.length > 0 ? remainingGrids[0].id : null);
      }
      
      // Update multiGridView to remove deleted grid - with defensive programming
      const activeGridIds = multiGridView?.activeGridIds;
      const activeGridIds_safe = activeGridIds;
      if (multiGridView && Array.isArray(activeGridIds_safe) && activeGridIds_safe.includes(gridId)) {
        safeSetMultiGridView({
          ...multiGridView,
          activeGridIds: (multiGridView.activeGridIds || []).filter(id => id !== gridId)
        });
      } else if (!multiGridView || !multiGridView.activeGridIds) {
        // Repair corrupted multiGridView state
        safeSetMultiGridView({
          activeGridIds: [],
          viewMode: 'tabs'
        });
      }

      // Refresh the shared grids source so the deletion is reflected immediately.
      await refetchGridsData();
    } catch (error) {
      throw error;
    }
  };

  const cloneGrid = async (gridId: string, newName: string): Promise<string> => {
    try {
      const originalGrid = allGrids.find(g => g.id === gridId);
      if (!originalGrid) throw new Error('Grid not found');
      
      const clonedGridData = {
        name: newName,
        description: `Cloned from ${originalGrid.name}`,
        grid_data: {
          default_class_minutes: originalGrid.defaultClassMinutes,
          lines: originalGrid.lines,
          study_periods: originalGrid.studyPeriods
        },
        curriculum_areas: originalGrid.curriculumAreas,
        classes: originalGrid.classes.map(cls => ({ ...cls })), // Deep clone classes
        grid_type_id: originalGrid.gridType?.id || null,
        is_active: true,
        status: 'draft',
        settings: {
          default_class_minutes: originalGrid.defaultClassMinutes,
          lines: originalGrid.lines,
          study_periods: originalGrid.studyPeriods
        }
      };
      
      const { data, error } = await supabase
        .from('grids')
        .insert([clonedGridData])
        .select()
        .single();

      if (error) throw error;
      // Refresh the shared grids source so the clone appears immediately.
      await refetchGridsData();
      return data.id;
    } catch (error) {
      throw error;
    }
  };

  const createGridType = async (gridType: Omit<GridType, 'id'>): Promise<string> => {
    try {
      const gridTypeData = {
        name: gridType.name,
        description: gridType.description || '',
        minutes_per_cycle: gridType.minutesPerCycle || 1400,
        periods_per_cycle: gridType.periodsPerCycle || 20,
        lines_per_period: gridType.linesPerPeriod || 7,
        is_system_default: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('grid_types')
        .insert([gridTypeData])
        .select()
        .single();
      
      if (error) throw error;
      return data.id;
    } catch (error) {
      throw error;
    }
  };

  const updateGridType = async (gridTypeId: string, updates: Partial<GridType>): Promise<void> => {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString()
      };
      
      // Map GridType properties to database columns
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.minutesPerCycle !== undefined) updateData.minutes_per_cycle = updates.minutesPerCycle;
      if (updates.periodsPerCycle !== undefined) updateData.periods_per_cycle = updates.periodsPerCycle;
      if (updates.linesPerPeriod !== undefined) updateData.lines_per_period = updates.linesPerPeriod;
      
      const { error } = await supabase
        .from('grid_types')
        .update(updateData)
        .eq('id', gridTypeId);
      
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const deleteGridType = async (gridTypeId: string): Promise<void> => {
    try {
      // Check if any grids are using this grid type
      const gridsUsingType = allGrids.filter(grid => grid.gridType?.id === gridTypeId);
      if (gridsUsingType.length > 0) {
        throw new Error(`Cannot delete grid type: ${gridsUsingType.length} grids are still using it`);
      }
      
      // Check if it's a system default
      const gridType = gridTypes.find(gt => gt.id === gridTypeId);
      if (gridType && (gridType as any).isSystemDefault) {
        throw new Error('Cannot delete system default grid types');
      }
      
      const { error } = await supabase
        .from('grid_types')
        .delete()
        .eq('id', gridTypeId);
      
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const canDeleteGridType = (gridTypeId: string): boolean => {
    const gridsUsingType = allGrids.filter(grid => grid.gridType?.id === gridTypeId);
    return gridsUsingType.length === 0;
  };

  const getGridStats = (gridId: string): GridStats | null => {
    const grid = allGrids.find(g => g.id === gridId);
    if (!grid) return null;

    const teacherAssignments = new Set<string>();
    let totalMinutes = 0;
    let unassignedClasses = 0;
    let totalStudents = 0;

    for (const classAllocation of grid.classes) {
      const assignedTeachers = Object.values(classAllocation.lineAssignments || {}).filter(Boolean);
      
      if (assignedTeachers.length === 0) {
        unassignedClasses++;
      } else {
        assignedTeachers.forEach(teacher => teacher && teacherAssignments.add(teacher));
      }

      totalStudents += classAllocation.studentCount || 0;
      // Would need to calculate minutes based on grid type and department
      totalMinutes += 70; // Placeholder
    }

    return {
      totalClasses: grid.classes.length,
      totalTeachers: teacherAssignments.size,
      totalMinutes,
      averageClassSize: grid.classes.length > 0 ? totalStudents / grid.classes.length : 0,
      unassignedClasses,
      teacherUtilization: {} // Would need teacher data to calculate
    };
  };

  const getAllGridsStats = (): GridStats => {
    let totalClasses = 0;
    let totalMinutes = 0;
    let unassignedClasses = 0;
    const allTeachers = new Set<string>();
    let totalStudents = 0;

    for (const grid of allGrids) {
      const stats = getGridStats(grid.id);
      if (stats) {
        totalClasses += stats.totalClasses;
        totalMinutes += stats.totalMinutes;
        unassignedClasses += stats.unassignedClasses;
        // Add teachers from this grid
      }
    }

    return {
      totalClasses,
      totalTeachers: allTeachers.size,
      totalMinutes,
      averageClassSize: totalClasses > 0 ? totalStudents / totalClasses : 0,
      unassignedClasses,
      teacherUtilization: {}
    };
  };

  const getCrossGridConflicts = (teacherCode?: string): CrossGridConflict[] => {
    const conflicts: CrossGridConflict[] = [];

    // This would need full implementation with teacher data
    // For now, return empty array

    return conflicts;
  };

  // Unified teacher assignment function with cross-grid conflict detection
  const assignTeacher = async (gridId: string, classId: string, lineId: string, teacherCode: string | null): Promise<void> => {
    try {

      // Find the target grid
      const targetGrid = allGrids.find(g => g.id === gridId);
      if (!targetGrid) {
        throw new Error(`Grid with ID ${gridId} not found`);
      }

      // Validate class exists in target grid
      const targetClass = targetGrid.classes.find(c => c.id === classId);
      if (!targetClass) {
        throw new Error(`Class ${classId} not found in grid ${gridId}`);
      }

      // Validate line exists in target grid
      const lineExists = targetGrid.lines?.some(line => {
        return typeof line === 'string' ? line === lineId : line?.id === lineId;
      });
      if (!lineExists) {
        throw new Error(`Line ${lineId} not found in grid ${gridId}`);
      }

      // Check for cross-grid conflicts if assigning a teacher
      if (teacherCode) {
        const conflicts = await validateCrossGridAssignment(gridId, classId, lineId, teacherCode);
        if (!conflicts.valid) {
          // Still proceed but log the conflicts
        }
      }

      // Write just this cell server-side rather than resending the whole class
      // array, which is how a stale copy used to delete other people's
      // assignments without any error (issue #218).
      await setLineAssignment(gridId, {
        classId,
        lineId,
        teacherCode,
        className: targetClass?.className ?? null,
        subject: targetClass?.subjectCode || targetClass?.className || null,
      });

      await refetchGridsData();

      // 🚀 REAL-TIME FIX: Notify TeacherLoadPanel of assignment changes
      assignmentEvents.notify();

    } catch (error) {
      throw error;
    }
  };

  // Batch teacher assignment function
  const batchAssignTeachers = async (gridId: string, assignments: Array<{ classId: string; lineId: string; teacherCode: string | null }>): Promise<void> => {
    try {

      // Find the target grid
      const targetGrid = allGrids.find(g => g.id === gridId);
      if (!targetGrid) {
        throw new Error(`Grid with ID ${gridId} not found`);
      }

      // Validate all assignments before applying any
      for (const assignment of assignments) {
        const targetClass = targetGrid.classes.find(c => c.id === assignment.classId);
        if (!targetClass) {
          throw new Error(`Class ${assignment.classId} not found in grid ${gridId}`);
        }

        const lineExists = targetGrid.lines?.some(line => {
          return typeof line === 'string' ? line === assignment.lineId : line?.id === assignment.lineId;
        });
        if (!lineExists) {
          throw new Error(`Line ${assignment.lineId} not found in grid ${gridId}`);
        }
      }

      // Apply all assignments in one server-side transaction (issue #218).
      await setLineAssignmentsBulk(
        gridId,
        assignments.map(({ classId, lineId, teacherCode }) => {
          const cls = targetGrid.classes.find(c => c.id === classId);
          return {
            classId,
            lineId,
            teacherCode,
            className: cls?.className ?? null,
            subject: cls?.subjectCode || cls?.className || null,
          };
        })
      );

      await refetchGridsData();

      // 🚀 REAL-TIME FIX: Notify TeacherLoadPanel of batch assignment changes
      assignmentEvents.notify();

    } catch (error) {
      throw error;
    }
  };

  // Cross-grid assignment validation
  const validateCrossGridAssignment = async (gridId: string, classId: string, lineId: string, teacherCode: string): Promise<{ valid: boolean; conflicts: string[] }> => {
    try {
      const conflicts: string[] = [];

      // Check if teacher is already assigned to the same line in other active grids
      for (const grid of allGrids) {
        if (grid.id === gridId || !grid.isActive) continue; // Skip target grid and inactive grids

        for (const cls of grid.classes) {
          const existingAssignment = cls.lineAssignments?.[lineId];
          if (existingAssignment === teacherCode) {
            conflicts.push(`Teacher ${teacherCode} is already assigned to line ${lineId} in grid "${grid.name}" (class: ${cls.className})`);
          }
        }
      }

      return {
        valid: conflicts.length === 0,
        conflicts
      };

    } catch (error) {
      return {
        valid: false,
        conflicts: ['Failed to validate cross-grid assignment']
      };
    }
  };

  const value: MultiGridContextType = {
    allGrids,
    gridsForYear,
    activeGrids,
    currentGrid,
    gridTypes,
    gridTemplates,
    multiGridView,
    setMultiGridView: safeSetMultiGridView,
    currentGridId,
    setCurrentGridId,
    createGrid,
    updateGrid,
    deleteGrid,
    cloneGrid,
    createGridType,
    updateGridType,
    deleteGridType,
    canDeleteGridType,
    assignTeacher,
    batchAssignTeachers,
    validateCrossGridAssignment,
    getGridStats,
    getAllGridsStats,
    getCrossGridConflicts,
    isLoading,
    error
  };

  return (
    <MultiGridContext.Provider value={value}>
      {children}
    </MultiGridContext.Provider>
  );
}

// Hook to use the MultiGrid context
export const useMultiGrid = () => {
  const context = useContext(MultiGridContext);
  if (context === undefined) {
    throw new Error('useMultiGrid must be used within a MultiGridProvider');
  }
  return context;
};

